import { useState } from "react";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Award, FileText, Users, Shield, BarChart3, CheckCircle, Mail, Lock, Loader2, Eye, EyeOff, ShieldCheck, Search, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LandingPage() {
  const { user, login, setUser, logout } = useAuth();
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!loginData.email || !loginData.password) {
      toast.error("Email and password are required");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/auth/login`,
        loginData,
        { withCredentials: true }
      );
      
      setUser(response.data);
      
      // Check if user needs to change password
      if (response.data.must_change_password) {
        window.location.href = '/change-password';
      } else {
        // Redirect based on saved path or role
        const savedRedirect = sessionStorage.getItem('auth_redirect');
        sessionStorage.removeItem('auth_redirect');
        if (savedRedirect) {
          window.location.href = savedRedirect;
        } else if (response.data.role === 'candidate') {
          window.location.href = '/my-application';
        } else {
          window.location.href = '/dashboard';
        }
      }
    } catch (error) {
      console.error("Login failed:", error);
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: FileText,
      title: "AI Document Analysis",
      description: "Automatically extract and analyze candidate evidence from uploaded documents using advanced OCR and NLP."
    },
    {
      icon: Award,
      title: "Smart Recommendations",
      description: "AI-powered qualification level recommendations based on competency framework matching."
    },
    {
      icon: Users,
      title: "Multi-Role Workflow",
      description: "Streamlined assessor scoring, IQA verification, and approval workflows."
    },
    {
      icon: Shield,
      title: "White-Label Ready",
      description: "Fully configurable for multiple institutions and IPCB organizations."
    },
    {
      icon: BarChart3,
      title: "Comprehensive Analytics",
      description: "Real-time insights into candidate progress, competency gaps, and assessment outcomes."
    },
    {
      icon: CheckCircle,
      title: "Dual Report Generation",
      description: "Generate professional IPCB evaluation and institutional competency reports."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <img 
                src="/credentia-ai-logo.png" 
                alt="Credentia AI" 
                className="h-[350px] w-auto object-contain"
              />
            </div>
            <div className="flex items-center gap-2">
              {user ? (
                <div className="flex items-center gap-3">
                  <span className="hidden sm:inline text-sm text-slate-600">
                    Signed in as <strong className="text-slate-900">{user.name || user.email}</strong>
                  </span>
                  <Button
                    data-testid="go-to-dashboard-btn"
                    onClick={() => {
                      const target = user.role === 'candidate' ? '/my-application' : '/dashboard';
                      window.location.href = target;
                    }}
                  >
                    Go to Dashboard
                  </Button>
                  <Button
                    data-testid="sign-out-btn"
                    variant="outline"
                    onClick={async () => {
                      await logout();
                      window.location.href = '/';
                    }}
                  >
                    Sign Out
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <Button data-testid="sign-in-btn" onClick={login}>
                    Sign In with Google
                  </Button>
                  <span className="text-slate-400">or</span>
                  <Button 
                    data-testid="email-login-btn" 
                    variant="outline"
                    onClick={() => setShowLoginForm(!showLoginForm)}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Email Login
                  </Button>
                </div>
              )}
            </div>
          </div>
          
          {/* Email/Password Login Form */}
          {showLoginForm && !user && (
            <div className="pb-4">
              <Card className="max-w-md ml-auto">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Sign In with Email</CardTitle>
                  <CardDescription>Enter your credentials to access the platform</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleEmailLogin} className="space-y-4" autoComplete="on">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="login-email"
                          name="email"
                          data-testid="login-email"
                          type="email"
                          autoComplete="username"
                          autoCapitalize="off"
                          autoCorrect="off"
                          spellCheck={false}
                          placeholder="your@email.com"
                          value={loginData.email}
                          onChange={(e) => setLoginData({ ...loginData, email: e.target.value.trim() })}
                          className="pl-10"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          id="login-password"
                          name="password"
                          data-testid="login-password"
                          type={showPassword ? "text" : "password"}
                          autoComplete="current-password"
                          autoCapitalize="off"
                          autoCorrect="off"
                          spellCheck={false}
                          placeholder="••••••••"
                          value={loginData.password}
                          onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                          className="pl-10 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Signing In...
                        </>
                      ) : (
                        "Sign In"
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="animate-fade-in">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-sm font-medium mb-6">
                <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                AI-Powered RPL Assessment
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight mb-6">
                Transform Prior Learning into{" "}
                <span className="text-blue-800">Recognized Credentials</span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-xl">
                Credentia AI streamlines Recognition of Prior Learning assessments with AI-powered document analysis, 
                competency mapping, and professional report generation for educational institutions.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button data-testid="get-started-btn" size="lg" onClick={login} className="px-8">
                  Get Started
                </Button>
                <Button 
                  data-testid="apply-now-btn" 
                  size="lg" 
                  variant="outline" 
                  className="px-8"
                  onClick={() => window.location.href = '/apply'}
                >
                  Apply as Candidate
                </Button>
                <Button 
                  data-testid="demo-tour-btn" 
                  size="lg" 
                  variant="ghost" 
                  className="px-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  onClick={() => window.location.href = '/demo'}
                >
                  See Product Tour
                </Button>
              </div>
              <div className="mt-10 flex items-center gap-8">
                <div>
                  <div className="text-3xl font-bold text-slate-900">500+</div>
                  <div className="text-sm text-slate-500">Candidates Assessed</div>
                </div>
                <div className="w-px h-12 bg-slate-200"></div>
                <div>
                  <div className="text-3xl font-bold text-slate-900">50+</div>
                  <div className="text-sm text-slate-500">Institutions</div>
                </div>
                <div className="w-px h-12 bg-slate-200"></div>
                <div>
                  <div className="text-3xl font-bold text-slate-900">95%</div>
                  <div className="text-sm text-slate-500">Accuracy Rate</div>
                </div>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative animate-fade-in" style={{ animationDelay: '200ms' }}>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1680444873773-7c106c23ac52?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjB1bml2ZXJzaXR5JTIwY2FtcHVzJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3MzQzODA2OHww&ixlib=rb-4.1.0&q=85"
                  alt="Modern University Campus"
                  className="w-full h-[400px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-slate-900">Assessment Complete</div>
                        <div className="text-xs text-slate-500">Master of Science Recommendation - 88% Confidence</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Floating elements */}
              <div className="absolute -top-4 -right-4 bg-white rounded-xl shadow-lg p-3 animate-bounce" style={{ animationDuration: '3s' }}>
                <Award className="w-8 h-8 text-amber-500" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          VERIFY CREDENTIAL — public CTA strip
          For students / employers / recruiters to verify the
          authenticity of any Credentia-issued credential in seconds.
          Designed to be visually distinct (emerald gradient) so it
          stands out from the institution-facing hero above.
          ============================================================ */}
      <section
        className="relative overflow-hidden bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900 text-white"
        data-testid="verify-section"
      >
        {/* Decorative QR-code pattern, very faint */}
        <svg
          className="absolute inset-0 w-full h-full opacity-[0.07] pointer-events-none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <defs>
            <pattern id="qrdots" x="0" y="0" width="32" height="32" patternUnits="userSpaceOnUse">
              <rect x="2" y="2" width="6" height="6" fill="currentColor" />
              <rect x="14" y="2" width="6" height="6" fill="currentColor" />
              <rect x="26" y="2" width="6" height="6" fill="currentColor" />
              <rect x="2" y="14" width="6" height="6" fill="currentColor" />
              <rect x="26" y="14" width="6" height="6" fill="currentColor" />
              <rect x="2" y="26" width="6" height="6" fill="currentColor" />
              <rect x="14" y="26" width="6" height="6" fill="currentColor" />
              <rect x="26" y="26" width="6" height="6" fill="currentColor" />
            </pattern>
          </defs>
          <rect x="0" y="0" width="100%" height="100%" fill="url(#qrdots)" />
        </svg>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            {/* Left: copy + CTA */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-emerald-100 text-xs font-medium mb-5">
                <ShieldCheck className="w-3.5 h-3.5" />
                Public Verification Service
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4">
                Verify any Credentia AI credential in seconds
              </h2>
              <p className="text-emerald-50/90 text-base sm:text-lg leading-relaxed mb-8 max-w-xl">
                Students, employers, recruiters and awarding bodies can confirm the authenticity of an
                RPL credential — instantly, anonymously, and free. Enter the Report ID printed on the
                certificate, or scan the QR code with your phone.
              </p>

              <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                <Button
                  size="lg"
                  onClick={() => window.location.href = '/verify'}
                  className="bg-white text-emerald-900 hover:bg-emerald-50 px-7 py-6 text-base font-semibold shadow-lg hover:shadow-xl transition-all"
                  data-testid="verify-cta-btn"
                >
                  <Search className="w-5 h-5 mr-2" />
                  Verify a Credential
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <a
                  href="/verify"
                  className="text-emerald-100 hover:text-white text-sm underline underline-offset-4 decoration-emerald-300/50"
                >
                  Learn more
                </a>
              </div>

              {/* trust micro-pills */}
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-emerald-100/80">
                <div className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5 text-emerald-300" /> Instant verification</div>
                <div className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5 text-emerald-300" /> Anonymous & free</div>
                <div className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5 text-emerald-300" /> No login required</div>
              </div>
            </div>

            {/* Right: graphic — stylised credential card with QR */}
            <div className="relative hidden lg:block">
              <VerifyHeroGraphic />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4">
              Comprehensive RPL Assessment Platform
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Everything you need to streamline Recognition of Prior Learning assessments 
              for your institution.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-slate-50 rounded-xl p-6 hover:shadow-lg transition-shadow duration-300 animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 bg-slate-900 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl font-bold text-white mb-4">
            Ready to Transform Your RPL Process?
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Join leading institutions using Credentia AI to streamline their assessment workflows.
          </p>
          <Button
            data-testid="cta-get-started-btn"
            size="lg"
            onClick={login}
            className="bg-white text-slate-900 hover:bg-slate-100 px-8"
          >
            Get Started Today
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-50 border-t border-slate-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center">
                <Award className="w-4 h-4 text-white" />
              </div>
              <span className="font-heading font-bold text-slate-900">Credentia AI</span>
            </div>
            <p className="text-sm text-slate-500">
              © {new Date().getFullYear()} Credentia AI. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

/**
 * Decorative graphic for the Verify Credential section.
 *
 * Layered, slightly skewed credential card with:
 *   - a stylised QR code (pure SVG, no asset dependency)
 *   - a "Verified" pill
 *   - a soft floating shield behind it
 * Pure CSS / inline SVG so it ships fast and renders crisp on any DPI.
 */
function VerifyHeroGraphic() {
  return (
    <div className="relative w-full max-w-md mx-auto" aria-hidden="true">
      {/* Floating shield behind the card */}
      <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-emerald-400/20 blur-2xl" />
      <div className="absolute -bottom-8 -left-8 w-40 h-40 rounded-full bg-teal-300/20 blur-3xl" />

      <div className="absolute -top-3 -right-3 w-16 h-16 rounded-2xl bg-emerald-500/30 backdrop-blur border border-white/30 flex items-center justify-center shadow-xl rotate-6">
        <ShieldCheck className="w-8 h-8 text-white" />
      </div>

      {/* The credential card */}
      <div className="relative bg-white rounded-2xl shadow-2xl p-6 transform -rotate-1 hover:rotate-0 transition-transform duration-500">
        {/* gold rule top */}
        <div className="h-[3px] -mx-6 -mt-6 mb-5 bg-gradient-to-r from-transparent via-amber-400 to-transparent" />

        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-emerald-700">
              Verified Credential
            </p>
            <p className="font-heading text-lg text-slate-900 mt-1 leading-snug">
              SWEP Diploma-Level Recognition
            </p>
            <p className="text-xs text-slate-500 mt-0.5">Ling Teck Ang · Diploma</p>
          </div>

          {/* Stylised QR code (pure SVG, deterministic pattern) */}
          <div className="shrink-0 p-2 bg-slate-50 rounded-lg border border-slate-200">
            <FauxQR />
          </div>
        </div>

        <div className="mt-5 flex items-center justify-between gap-3">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px] font-semibold">
            <ShieldCheck className="w-3 h-3" />
            VERIFIED
          </div>
          <div className="font-mono text-[10px] text-slate-500 tracking-wider">
            IPCB-2026-XXX-0001
          </div>
        </div>

        {/* gold rule bottom */}
        <div className="h-[3px] -mx-6 -mb-6 mt-5 bg-gradient-to-r from-transparent via-amber-400 to-transparent" />
      </div>
    </div>
  );
}

/**
 * Pure-SVG QR-like graphic. Deterministic, no library needed — it doesn't
 * need to actually scan, it just needs to *look* like a credential QR
 * for the marketing graphic.
 */
function FauxQR() {
  const SIZE = 64;
  const CELL = 8;
  // Static 8x8 truthy/falsy matrix that looks like a QR code with the
  // three finder markers in the corners.
  // prettier-ignore
  const matrix = [
    [1,1,1,1,1,1,1,1],
    [1,0,0,0,1,0,0,1],
    [1,0,1,1,0,0,1,1],
    [1,0,0,1,1,0,0,1],
    [1,0,1,0,0,1,0,1],
    [1,1,0,1,1,0,1,1],
    [1,0,0,0,1,1,0,1],
    [1,1,1,1,1,1,1,1],
  ];
  const finder = (cx, cy) => (
    <g key={`f-${cx}-${cy}`}>
      <rect x={cx} y={cy} width={CELL * 3} height={CELL * 3} fill="#0f172a" />
      <rect x={cx + CELL * 0.5} y={cy + CELL * 0.5} width={CELL * 2} height={CELL * 2} fill="#fff" />
      <rect x={cx + CELL} y={cy + CELL} width={CELL} height={CELL} fill="#0f172a" />
    </g>
  );
  return (
    <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
      {matrix.flatMap((row, y) =>
        row.map((v, x) => v ? <rect key={`${x}-${y}`} x={x * CELL} y={y * CELL} width={CELL} height={CELL} fill="#0f172a" /> : null)
      )}
      {finder(0, 0)}
      {finder(SIZE - CELL * 3, 0)}
      {finder(0, SIZE - CELL * 3)}
    </svg>
  );
}

